import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PenIcon } from "assets/icons";
import { Button, AlertMessage, CheckBox } from "components/atoms";
import {
  TextInput,
  MessageTextAreaInput,
  GeneralSelectInput,
  TabbedFormToggle,
  TabConfig,
  EditTabConfig,
  DatePickerInput,
  MultiChoiceCheckboxGroup,
  InformationItem,
} from "components/molecules";
import { useUserData, useContentSanitizer } from "hooks";
import { ProfileHandling, formatDate, formatDateToString } from "services";
import { employmentStatusOptions, courseReasonOptions } from "types";

const employmentSchema = Yup.object({
  currentEmploymentStatus: Yup.string().required(
    "Please select your current employment status"
  ),
  reasonForUndertakingCourse: Yup.array().min(
    1,
    "Please select at least one reason"
  ),
  reasonOtherDetails: Yup.string()
    .nullable()
    .when("reasonForUndertakingCourse", {
      is: (reasons: string[]) => reasons?.includes("other"),
      then: (schema) => schema.required("Please specify other reason"),
      otherwise: (schema) => schema.nullable(),
    }),

  employment1CompanyName: Yup.string().test(
    "conditional-required",
    "Please enter company name",
    function (value) {
      const {
        employment1PositionAndDuties,
        employment1DateCommenced,
        employment1DateEnded,
      } = this.parent;
      const hasAnyValue = [
        employment1PositionAndDuties,
        employment1DateCommenced,
        employment1DateEnded,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  employment1PositionAndDuties: Yup.string().test(
    "conditional-required",
    "Please enter position and duties",
    function (value) {
      const {
        employment1CompanyName,
        employment1DateCommenced,
        employment1DateEnded,
      } = this.parent;
      const hasAnyValue = [
        employment1CompanyName,
        employment1DateCommenced,
        employment1DateEnded,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  employment1DateCommenced: Yup.date()
    .nullable()
    .test(
      "conditional-required",
      "Please provide start date",
      function (value) {
        const {
          employment1CompanyName,
          employment1PositionAndDuties,
          employment1DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment1CompanyName,
          employment1PositionAndDuties,
          employment1DateEnded,
        ].some((val) => {
          if (val instanceof Date) return true;
          return val && val !== "";
        });
        return hasAnyValue ? !!value : true;
      }
    ),
  employment1DateEnded: Yup.date()
    .nullable()
    .test("conditional-required", "Please provide end date", function (value) {
      const {
        employment1CompanyName,
        employment1PositionAndDuties,
        employment1DateCommenced,
      } = this.parent;
      const hasAnyValue = [
        employment1CompanyName,
        employment1PositionAndDuties,
        employment1DateCommenced,
      ].some((val) => {
        if (val instanceof Date) return true;
        return val && val !== "";
      });
      return hasAnyValue ? !!value : true;
    }),

  employment2CompanyName: Yup.string().test(
    "conditional-required",
    "Please enter company name",
    function (value) {
      const {
        employment2PositionAndDuties,
        employment2DateCommenced,
        employment2DateEnded,
      } = this.parent;
      const hasAnyValue = [
        employment2PositionAndDuties,
        employment2DateCommenced,
        employment2DateEnded,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  employment2PositionAndDuties: Yup.string().test(
    "conditional-required",
    "Please enter position and duties",
    function (value) {
      const {
        employment2CompanyName,
        employment2DateCommenced,
        employment2DateEnded,
      } = this.parent;
      const hasAnyValue = [
        employment2CompanyName,
        employment2DateCommenced,
        employment2DateEnded,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  employment2DateCommenced: Yup.date()
    .nullable()
    .test(
      "conditional-required",
      "Please provide start date",
      function (value) {
        const {
          employment2CompanyName,
          employment2PositionAndDuties,
          employment2DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment2CompanyName,
          employment2PositionAndDuties,
          employment2DateEnded,
        ].some((val) => {
          if (val instanceof Date) return true;
          return val && val !== "";
        });
        return hasAnyValue ? !!value : true;
      }
    ),
  employment2DateEnded: Yup.date()
    .nullable()
    .test("conditional-required", "Please provide end date", function (value) {
      const {
        employment2CompanyName,
        employment2PositionAndDuties,
        employment2DateCommenced,
      } = this.parent;
      const hasAnyValue = [
        employment2CompanyName,
        employment2PositionAndDuties,
        employment2DateCommenced,
      ].some((val) => {
        if (val instanceof Date) return true;
        return val && val !== "";
      });
      return hasAnyValue ? !!value : true;
    }),

  employment3CompanyName: Yup.string().test(
    "conditional-required",
    "Please enter company name",
    function (value) {
      const {
        employment3PositionAndDuties,
        employment3DateCommenced,
        employment3DateEnded,
      } = this.parent;
      const hasAnyValue = [
        employment3PositionAndDuties,
        employment3DateCommenced,
        employment3DateEnded,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  employment3PositionAndDuties: Yup.string().test(
    "conditional-required",
    "Please enter position and duties",
    function (value) {
      const {
        employment3CompanyName,
        employment3DateCommenced,
        employment3DateEnded,
      } = this.parent;
      const hasAnyValue = [
        employment3CompanyName,
        employment3DateCommenced,
        employment3DateEnded,
      ].some((val) => val && val !== "");
      return hasAnyValue ? !!value : true;
    }
  ),
  employment3DateCommenced: Yup.date()
    .nullable()
    .test(
      "conditional-required",
      "Please provide start date",
      function (value) {
        const {
          employment3CompanyName,
          employment3PositionAndDuties,
          employment3DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment3CompanyName,
          employment3PositionAndDuties,
          employment3DateEnded,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
  employment3DateEnded: Yup.date()
    .nullable()
    .test("conditional-required", "Please provide end date", function (value) {
      const {
        employment3CompanyName,
        employment3PositionAndDuties,
        employment3DateCommenced,
      } = this.parent;
      const hasAnyValue = [
        employment3CompanyName,
        employment3PositionAndDuties,
        employment3DateCommenced,
      ].some((val) => {
        if (val instanceof Date) return true;
        return val && val !== "";
      });
      return hasAnyValue ? !!value : true;
    }),
});

const EmploymentHistoryPage: React.FC = () => {
  const { userData, fetchUserData } = useUserData();
  const { sanitizeContent, sanitizeObject } = useContentSanitizer();
  const [editingTab, setEditingTab] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      currentEmploymentStatus: "",

      employment1CompanyName: "",
      employment1PositionAndDuties: "",
      employment1DateCommenced: null as Date | null,
      employment1DateEnded: null as Date | null,
      employment1IsStillWorking: false,

      employment2CompanyName: "",
      employment2PositionAndDuties: "",
      employment2DateCommenced: null as Date | null,
      employment2DateEnded: null as Date | null,
      employment2IsStillWorking: false,

      employment3CompanyName: "",
      employment3PositionAndDuties: "",
      employment3DateCommenced: null as Date | null,
      employment3DateEnded: null as Date | null,
      employment3IsStillWorking: false,

      reasonForUndertakingCourse: [] as string[],
      reasonOtherDetails: "",
    },
    validationSchema: employmentSchema,
    onSubmit: () => {},
  });

  const resetFormToUserData = () => {
    if (!userData) return;

    const employmentData = [
      userData?.employment1,
      userData?.employment2,
      userData?.employment3,
    ];

    formik.setValues({
      currentEmploymentStatus:
        userData?.employmentDetails?.currentEmploymentStatus || "",

      employment1CompanyName: employmentData[0]?.companyName || "",
      employment1PositionAndDuties: employmentData[0]?.positionAndDuties || "",
      employment1DateCommenced: employmentData[0]?.dateCommenced
        ? new Date(employmentData[0].dateCommenced)
        : null,
      employment1DateEnded: employmentData[0]?.dateEnded
        ? new Date(employmentData[0].dateEnded)
        : null,
      employment1IsStillWorking: employmentData[0]?.isStillWorking || false,

      employment2CompanyName: employmentData[1]?.companyName || "",
      employment2PositionAndDuties: employmentData[1]?.positionAndDuties || "",
      employment2DateCommenced: employmentData[1]?.dateCommenced
        ? new Date(employmentData[1].dateCommenced)
        : null,
      employment2DateEnded: employmentData[1]?.dateEnded
        ? new Date(employmentData[1].dateEnded)
        : null,
      employment2IsStillWorking: employmentData[1]?.isStillWorking || false,

      employment3CompanyName: employmentData[2]?.companyName || "",
      employment3PositionAndDuties: employmentData[2]?.positionAndDuties || "",
      employment3DateCommenced: employmentData[2]?.dateCommenced
        ? new Date(employmentData[2].dateCommenced)
        : null,
      employment3DateEnded: employmentData[2]?.dateEnded
        ? new Date(employmentData[2].dateEnded)
        : null,
      employment3IsStillWorking: employmentData[2]?.isStillWorking || false,

      reasonForUndertakingCourse:
        userData?.employmentDetails?.reasonForUndertakingCourse || [],
      reasonOtherDetails: userData?.employmentDetails?.reasonOtherDetails || "",
    });
  };

  const getFormValue = (key: string) =>
    formik.values[key as keyof typeof formik.values];

  const isEmploymentOverviewComplete = () => {
    const reasons = getFormValue("reasonForUndertakingCourse") as string[];
    const hasReasons = reasons && reasons.length > 0;
    const hasOtherDetails = reasons?.includes("other")
      ? !!getFormValue("reasonOtherDetails")
      : true;
    return (
      !!getFormValue("currentEmploymentStatus") && hasReasons && hasOtherDetails
    );
  };

  const isEmploymentComplete = (level: number) => {
    const prefix = `employment${level}`;
    return !!(
      getFormValue(`${prefix}CompanyName`) &&
      getFormValue(`${prefix}PositionAndDuties`) &&
      getFormValue(`${prefix}DateCommenced`)
    );
  };

  const shouldShowEmployment1 = () => {
    const hasExistingData =
      userData?.employment1 &&
      (userData.employment1.companyName ||
        userData.employment1.positionAndDuties ||
        userData.employment1.dateCommenced ||
        userData.employment1.dateEnded);

    return hasExistingData || isEmploymentOverviewComplete();
  };

  const shouldShowEmployment2 = () => {
    const hasExistingData =
      userData?.employment2 &&
      (userData.employment2.companyName ||
        userData.employment2.positionAndDuties ||
        userData.employment2.dateCommenced ||
        userData.employment2.dateEnded);

    const employment1HasAnyData = shouldShowEmployment1();

    return hasExistingData || employment1HasAnyData;
  };

  const shouldShowEmployment3 = () => {
    const hasExistingData =
      userData?.employment3 &&
      (userData.employment3.companyName ||
        userData.employment3.positionAndDuties ||
        userData.employment3.dateCommenced ||
        userData.employment3.dateEnded);

    const employment2HasAnyData = shouldShowEmployment2();

    return hasExistingData || employment2HasAnyData;
  };

  const validateFields = async (fields: string[]): Promise<boolean> => {
    const allErrors = await formik.validateForm();
    const relevantErrors = Object.keys(allErrors).filter((key) =>
      fields.includes(key)
    );
    if (relevantErrors.length > 0) {
      const touchedFields = fields.reduce(
        (acc: { [key: string]: boolean }, field: string) => ({
          ...acc,
          [field]: true,
        }),
        {}
      );
      formik.setTouched(touchedFields);
      setErrorMessage("Please fix the validation errors before saving.");
      return false;
    }
    return true;
  };

  const saveEmploymentOverview = async (): Promise<boolean> => {
    try {
      const fields = [
        "currentEmploymentStatus",
        "reasonForUndertakingCourse",
        "reasonOtherDetails",
      ];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        employmentDetails: {
          currentEmploymentStatus: sanitizeContent(
            getFormValue("currentEmploymentStatus") as string
          ),
          reasonForUndertakingCourse: sanitizeObject(
            getFormValue("reasonForUndertakingCourse") as string[]
          ),
          reasonOtherDetails: sanitizeContent(
            getFormValue("reasonOtherDetails") as string
          ),
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Employment overview updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update employment overview.");
      setSuccessMessage("");
      return false;
    }
  };

  const saveEmployment = (level: number) => async (): Promise<boolean> => {
    try {
      const prefix = `employment${level}`;
      const fields = [
        `${prefix}CompanyName`,
        `${prefix}PositionAndDuties`,
        `${prefix}DateCommenced`,
      ];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        [`employment${level}`]: {
          companyName: sanitizeContent(
            getFormValue(`${prefix}CompanyName`) as string
          ),
          positionAndDuties: sanitizeContent(
            getFormValue(`${prefix}PositionAndDuties`) as string
          ),
          dateCommenced: getFormValue(`${prefix}DateCommenced`)
            ? formatDate(getFormValue(`${prefix}DateCommenced`) as Date)
            : null,
          dateEnded: getFormValue(`${prefix}DateEnded`)
            ? formatDate(getFormValue(`${prefix}DateEnded`) as Date)
            : null,
          isStillWorking: getFormValue(`${prefix}IsStillWorking`),
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage(
        `${level === 1 ? "Most recent" : level === 2 ? "2nd" : "3rd"} employment updated successfully.`
      );
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage(
        `Failed to update ${level === 1 ? "most recent" : level === 2 ? "2nd" : "3rd"} employment.`
      );
      setSuccessMessage("");
      return false;
    }
  };

  const handleCurrentlyWorkingChange = async (
    field: string,
    checked: boolean
  ) => {
    if (checked) {
      if (field === "employment1IsStillWorking") {
        formik.setFieldValue("employment1IsStillWorking", true);
        formik.setFieldValue("employment1DateEnded", null);
        formik.setFieldValue("employment2IsStillWorking", false);
        formik.setFieldValue("employment3IsStillWorking", false);
      }
    } else {
      formik.setFieldValue(field, false);
    }
  };

  const createEmploymentTab = (level: number) => {
    const prefix = `employment${level}`;
    const labels = [
      "Most Recent Employment",
      "2nd Employment",
      "3rd Employment",
    ];
    const descriptions = [
      "Current or most recent job",
      "Previous employment",
      "Earlier employment",
    ];

    return {
      id: prefix,
      label: labels[level - 1],
      description: descriptions[level - 1],
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>{labels[level - 1]}</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab(prefix)}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Company Name:"
              value={String(
                getFormValue(`${prefix}CompanyName`) || "Not Provided"
              )}
            />
            <InformationItem
              label="Position and Duties:"
              value={String(
                getFormValue(`${prefix}PositionAndDuties`) || "Not Provided"
              )}
            />
            <InformationItem
              label="Date Commenced:"
              value={
                getFormValue(`${prefix}DateCommenced`)
                  ? formatDateToString(
                      getFormValue(`${prefix}DateCommenced`) as Date
                    )
                  : "Not Provided"
              }
            />
            <InformationItem
              label="Date Ended:"
              value={
                getFormValue(`${prefix}DateEnded`)
                  ? formatDateToString(
                      getFormValue(`${prefix}DateEnded`) as Date
                    )
                  : level === 1
                    ? "Current Position"
                    : "Not Provided"
              }
            />
          </dl>
        </div>
      ),
    };
  };

  const createEmploymentEditTab = (level: number) => {
    const prefix = `employment${level}`;
    const labels = [
      "Most Recent Employment",
      "2nd Employment",
      "3rd Employment",
    ];

    return {
      id: prefix,
      label: labels[level - 1],
      title: `Edit ${labels[level - 1]}`,
      onSave: saveEmployment(level),
      saveDisabled: !isEmploymentComplete(level),
      formFields: (
        <>
          <TextInput
            id={`${prefix}CompanyName`}
            label="Company Name *"
            placeholder="Enter company name"
            getFieldProps={() => ({
              value: getFormValue(`${prefix}CompanyName`),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={
              formik.errors[
                `${prefix}CompanyName` as keyof typeof formik.errors
              ] as string
            }
            touched={
              formik.touched[
                `${prefix}CompanyName` as keyof typeof formik.touched
              ]
            }
          />
          <MessageTextAreaInput
            id={`${prefix}PositionAndDuties`}
            label="Position and Duties *"
            placeholder="Describe your position and main duties/responsibilities"
            rows="4"
            maxLength={500}
            getFieldProps={() => ({
              value: getFormValue(`${prefix}PositionAndDuties`),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={
              formik.errors[
                `${prefix}PositionAndDuties` as keyof typeof formik.errors
              ] as string
            }
            touched={
              formik.touched[
                `${prefix}PositionAndDuties` as keyof typeof formik.touched
              ]
            }
          />
          <DatePickerInput
            id={`${prefix}DateCommenced`}
            className={`${prefix}DateCommenced`}
            label="Date Commenced *"
            value={getFormValue(`${prefix}DateCommenced`) as Date | null}
            onChange={(date) =>
              formik.setFieldValue(`${prefix}DateCommenced`, date)
            }
            preset="pastDate"
          />
          <DatePickerInput
            id={`${prefix}DateEnded`}
            className={`${prefix}DateEnded`}
            label="Date Ended"
            value={getFormValue(`${prefix}DateEnded`) as Date | null}
            disabled={getFormValue(`${prefix}IsStillWorking`) as boolean}
            onChange={(date) =>
              formik.setFieldValue(`${prefix}DateEnded`, date)
            }
            preset="pastDate"
          />
          <CheckBox
            label="Currently working here"
            checked={getFormValue(`${prefix}IsStillWorking`) as boolean}
            disabled={level !== 1}
            onChange={(checked) =>
              handleCurrentlyWorkingChange(`${prefix}IsStillWorking`, checked)
            }
          />
        </>
      ),
    };
  };

  useEffect(() => {
    if (!userData) return;

    const employmentData = [
      userData?.employment1,
      userData?.employment2,
      userData?.employment3,
    ];

    formik.setValues({
      currentEmploymentStatus:
        userData?.employmentDetails?.currentEmploymentStatus || "",

      employment1CompanyName: employmentData[0]?.companyName || "",
      employment1PositionAndDuties: employmentData[0]?.positionAndDuties || "",
      employment1DateCommenced: employmentData[0]?.dateCommenced
        ? new Date(employmentData[0].dateCommenced)
        : null,
      employment1DateEnded: employmentData[0]?.dateEnded
        ? new Date(employmentData[0].dateEnded)
        : null,
      employment1IsStillWorking: employmentData[0]?.isStillWorking || false,

      employment2CompanyName: employmentData[1]?.companyName || "",
      employment2PositionAndDuties: employmentData[1]?.positionAndDuties || "",
      employment2DateCommenced: employmentData[1]?.dateCommenced
        ? new Date(employmentData[1].dateCommenced)
        : null,
      employment2DateEnded: employmentData[1]?.dateEnded
        ? new Date(employmentData[1].dateEnded)
        : null,
      employment2IsStillWorking: employmentData[1]?.isStillWorking || false,

      employment3CompanyName: employmentData[2]?.companyName || "",
      employment3PositionAndDuties: employmentData[2]?.positionAndDuties || "",
      employment3DateCommenced: employmentData[2]?.dateCommenced
        ? new Date(employmentData[2].dateCommenced)
        : null,
      employment3DateEnded: employmentData[2]?.dateEnded
        ? new Date(employmentData[2].dateEnded)
        : null,
      employment3IsStillWorking: employmentData[2]?.isStillWorking || false,

      reasonForUndertakingCourse:
        userData?.employmentDetails?.reasonForUndertakingCourse || [],
      reasonOtherDetails: userData?.employmentDetails?.reasonOtherDetails || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const displayTabs: TabConfig[] = [
    {
      id: "employment-overview",
      label: "Employment Overview",
      description: "Current employment status and study motivation",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Employment Overview</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("employment-overview")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Current Employment Status:"
              value={
                employmentStatusOptions.find(
                  (option) =>
                    option.value === getFormValue("currentEmploymentStatus")
                )?.label || "Not Provided"
              }
            />
            <InformationItem
              label="Reasons for Undertaking Course:"
              value={
                (getFormValue("reasonForUndertakingCourse") as string[])
                  ?.length > 0 ? (
                  <ol>
                    {(
                      getFormValue("reasonForUndertakingCourse") as string[]
                    ).map((reason) => (
                      <li key={reason}>
                        {courseReasonOptions.find(
                          (option) => option.value === reason
                        )?.label || reason}
                      </li>
                    ))}
                  </ol>
                ) : (
                  "Not Provided"
                )
              }
            />
            {(getFormValue("reasonForUndertakingCourse") as string[])?.includes(
              "other"
            ) && (
              <InformationItem
                label="Other Details:"
                value={String(
                  getFormValue("reasonOtherDetails") || "Not Provided"
                )}
              />
            )}
          </dl>
        </div>
      ),
    },
    ...(shouldShowEmployment1() ? [createEmploymentTab(1)] : []),
    ...(shouldShowEmployment2() ? [createEmploymentTab(2)] : []),
    ...(shouldShowEmployment3() ? [createEmploymentTab(3)] : []),
  ];

  const editTabConfigs: EditTabConfig[] = [
    {
      id: "employment-overview",
      label: "Employment Overview",
      title: "Edit Employment Overview",
      onSave: saveEmploymentOverview,
      onCancel: resetFormToUserData,
      saveDisabled: !isEmploymentOverviewComplete(),
      formFields: (
        <>
          <GeneralSelectInput
            id="currentEmploymentStatus"
            label="Current Employment Status *"
            options={employmentStatusOptions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={getFormValue("currentEmploymentStatus") as string}
            error={formik.errors.currentEmploymentStatus}
            touched={formik.touched.currentEmploymentStatus}
          />
          <MultiChoiceCheckboxGroup
            name="reasonForUndertakingCourse"
            label="Main reason for undertaking course"
            options={courseReasonOptions}
            value={getFormValue("reasonForUndertakingCourse") as string[]}
            otherValue={getFormValue("reasonOtherDetails") as string}
            onChange={(values) =>
              formik.setFieldValue("reasonForUndertakingCourse", values)
            }
            onOtherChange={(value) =>
              formik.setFieldValue("reasonOtherDetails", value)
            }
            showOther={true}
            otherPlaceholder="Please specify"
            required={true}
          />
        </>
      ),
    },
    ...(shouldShowEmployment1()
      ? [
          {
            ...createEmploymentEditTab(1),
            onCancel: resetFormToUserData,
          },
        ]
      : []),
    ...(shouldShowEmployment2()
      ? [
          {
            ...createEmploymentEditTab(2),
            onCancel: resetFormToUserData,
          },
        ]
      : []),
    ...(shouldShowEmployment3()
      ? [
          {
            ...createEmploymentEditTab(3),
            onCancel: resetFormToUserData,
          },
        ]
      : []),
  ];

  return (
    <div className="form-group employment-history-page">
      {successMessage && (
        <AlertMessage type="success">{successMessage}</AlertMessage>
      )}
      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}
      <TabbedFormToggle
        headerComponent={
          <div className="form-group header">
            <h2>Employment History</h2>
          </div>
        }
        displayTabs={displayTabs}
        editTabConfigs={editTabConfigs}
        formikInstance={formik}
        editingTab={editingTab}
        onTabEditToggle={setEditingTab}
      />
    </div>
  );
};

export default EmploymentHistoryPage;
